import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box } from '@iheartjane/reefer';
import omit from 'lodash-es/omit';
import { useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useLocation, useNavigate } from 'react-router';

import type { AlgoliaProduct } from '@jane/search/types';
import {
  buildClickTrackingEvent,
  findSpecialForProduct,
} from '@jane/shared-ecomm/components';
import { useDeepCompareMemo } from '@jane/shared-ecomm/hooks';
import { useUserPreferences } from '@jane/shared-ecomm/providers';
import {
  track,
  trackProductListingTap,
  useMoengage,
} from '@jane/shared-ecomm/tracking';
import {
  LegacyMenuProductCard,
  productDetailLink,
} from '@jane/shared/components';
import type { LegacyMenuProductCardProps } from '@jane/shared/components';
import { useUserSegmentIds } from '@jane/shared/data-access';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, StoreSpecial } from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import { Zone } from '@jane/shared/types';
import {
  deserializeCannabinoids,
  postMessageToIframeParent,
} from '@jane/shared/util';

import { useCustomerDispatch } from '../../../customer/dispatch';
import { selectBundlePossibilities } from '../../../customer/redux/bundlePossibilities';
import {
  attemptToAddToCart,
  closeCart,
  deleteCartItem,
  selectCart,
} from '../../../customer/redux/cart';
import { useCustomerSelector } from '../../../customer/selectors';
import { BundleSpecialEnticementContent } from '../../bundleSpecialEnticementContent';
import { EnticementBubble } from '../../enticementBubble';
import styles from './cartToppers.module.css';

const DEFAULT_PRODUCT_CARD_WIDTH = 200;

type HitProductCardProps = Omit<
  LegacyMenuProductCardProps,
  | 'appMode'
  | 'brand'
  | 'cartIsOpen'
  | 'cartProducts'
  | 'cartStoreId'
  | 'currentSpecial'
  | 'itemWidth'
  | 'onClick'
  | 'product'
  | 'userLocation'
> & {
  algoliaIndexName: string;
  bucketName?: string;
  carouselView?: boolean;
  columnPosition?: number;
  hit: AlgoliaProduct;
  itemWidth?: number;
  productLocation?: string;
  rowPosition?: number;
  specials?: StoreSpecial[];
  zone: Zone;
};

export const HitProductCard = ({
  algoliaIndexName,
  bucketName,
  columnPosition,
  hit,
  productLocation,
  rowPosition,
  specials,
  zone,
  productInstance,
  ...props
}: HitProductCardProps) => {
  const navigate = useNavigate();
  const dispatch = useCustomerDispatch();
  const { moeTrack } = useMoengage();
  const { userLocation, userPreferences = {} } = useUserPreferences();
  const { storeFulfillmentType, storeSearchRadius, storeType } =
    userPreferences;
  const { pathname } = useLocation();
  const { appMode } = useEcommApp();
  const c1BundleTag = useFlag(FLAGS.c1BundleTag);

  const { cart } = useCustomerSelector(selectCart);
  const cartStore = cart.store;
  const cartIsOpen = cart.is_open;
  const cartProducts = cart.products;
  const cartStoreId = cartStore?.id;

  const { touchedProductId, bundlePossibilities } = useCustomerSelector(
    selectBundlePossibilities
  );

  const { store, carouselView, listView, itemWidth } = props;

  const {
    city: storeCity,
    state: storeState,
    name: storeName,
    id: storeId,
  } = store;

  const [bundleSpecial, setBundleSpecial] = useState<StoreSpecial | undefined>(
    undefined
  );

  const menuProduct = useDeepCompareMemo<any>(() => {
    const mp = {
      ...deserializeCannabinoids(hit, 'menuProduct'),
      id: hit['product_id'],
    };
    return omit(mp, 'product_id');
  }, [hit]);

  useEffect(() => {
    const matchesTouchedProduct = touchedProductId === menuProduct.id;
    const bundleSpecial =
      specials && specials.length > 0 && matchesTouchedProduct
        ? specials.find((s) => s.id === bundlePossibilities.special_id)
        : undefined;

    setBundleSpecial(bundleSpecial);
  }, [touchedProductId, bundlePossibilities]);

  const userSegments = useUserSegmentIds();

  const clickTrackingEvent = buildClickTrackingEvent({
    indexName: algoliaIndexName,
    store,
    menuProduct,
    carouselView,
    rowPosition,
    columnPosition,
    listView,
    menuRowName: bucketName,
    userSegments,
  });

  const surfaceHeadlessCartTopperEvents = () => {
    postMessageToIframeParent({
      messageType: 'analyticsEvent',
      payload: {
        name: 'cartTopperItemClickedInCheckout',
        properties: {
          productId: menuProduct.id,
        },
      },
    });

    postMessageToIframeParent({
      messageType: 'navigationEvent',
      payload: {
        name: 'navigateToProduct',
        link: productDetailLink({
          product: menuProduct,
          appMode,
          store,
          brand: menuProduct.brand,
        }),
      },
    });
  };

  const enticementBubbleWidth = itemWidth || DEFAULT_PRODUCT_CARD_WIDTH;

  const hideEnticements = zone === Zone.Cart;

  return (
    <InView
      className={styles['inViewFlex']}
      data-testid="hit-product-card"
      delay={1000}
      onChange={(isInView) => {
        if (isInView) {
          productInstance?.impress();
        }
      }}
      threshold={0.5}
      triggerOnce
    >
      <LegacyMenuProductCard
        appMode={appMode}
        cartIsOpen={cartIsOpen}
        cartProducts={cartProducts}
        cartStoreId={cartStoreId}
        currentSpecial={findSpecialForProduct(
          menuProduct,
          specials,
          c1BundleTag
        )}
        product={menuProduct}
        productInstance={productInstance}
        onAddToCart={(addData) => {
          dispatch(
            attemptToAddToCart({
              ...addData,
              menuProduct: addData.menuProduct as MenuProduct,
              moeTrack,
              navigate,
              source: pathname,
              userPreferences: {
                cityState: userLocation.cityState,
                storeFulfillmentType: userPreferences.storeFulfillmentType,
                storeSearchRadius: userPreferences.storeSearchRadius,
                storeType: userPreferences.storeType,
                zipcode: userLocation.zipcode,
              },
              userSegments,
            })
          );
          productInstance?.click({ isAddToCart: true });
        }}
        onDeleteFromCart={(deleteData) => {
          dispatch(
            deleteCartItem(deleteData.itemId, deleteData.selectedWeight)
          );
        }}
        onCloseCart={() => dispatch(closeCart())}
        onClick={() => {
          productInstance?.click();
          if (bucketName === 'cart-toppers' && appMode === 'headless') {
            surfaceHeadlessCartTopperEvents();
          }
          if (clickTrackingEvent) track(clickTrackingEvent);
          trackProductListingTap({
            cityState: userLocation?.cityState,
            product: menuProduct,
            productLocation,
            source: pathname,
            storeId: String(storeId),
            storeName,
            storeCity,
            storeState,
            storeFulfillmentType,
            storeSearchRadius,
            storeType,
            userSegments,
            zipcode: userLocation?.zipcode,
          });
        }}
        {...props}
        store={store}
        userLocation={userLocation}
      />
      {!hideEnticements && bundleSpecial && !listView && (
        <Box
          px={8}
          maxWidth={enticementBubbleWidth}
          minWidth={enticementBubbleWidth}
        >
          <EnticementBubble onClose={() => setBundleSpecial(undefined)}>
            <BundleSpecialEnticementContent
              bundlePossibilities={bundlePossibilities}
              special={bundleSpecial}
              store={store}
            />
          </EnticementBubble>
        </Box>
      )}
    </InView>
  );
};
